import { AssignmentDoc, CalendarDoc, ResourceDoc, WorkLogDoc } from 'common';
import { ConcourseDb } from '../ConcourseDb';
import { Task, TaskType } from 'src/components/concourse-schedule/model/Task';
import { ResourceType } from 'src/components';

export class ScheduleWorkService {
  /*
  // Just in case we need to reactivate this
  // Now we access to p-${projectId}-calendars
  static getCalendarDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-calendars`);
  }
  static saveCalendar(projectId: string, calendar: CalendarDoc): Promise<CalendarDoc> {
    const db = this.getCalendarDb(projectId);
    return db.saveDoc(calendar);
  }
  */

  static getResourcesDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-resources`);
  }
  static async saveResource(projectId: string, resource: ResourceDoc): Promise<ResourceDoc> {
    const db = this.getResourcesDb(projectId);
    return await db.saveDoc(resource);
    }
    /*
  static async getResourceDocs(projectId: string): Promise<ResourceDoc[]> {
    const db = this.getResourcesDb(projectId);
    const files = await db.getDocs();
    return files.filter((file: ResourceDoc) => !file.deleted_date);
  }
  static async getResourcesInSection(projectId: string, resourceType: ResourceType): Promise<ResourceDoc[]> {
    const db = this.getResourcesDb(projectId);
    const resources = await db.getDocs();
    const resourcesFiltered = resources.filter((resource: ResourceDoc) => !resource.deleted_date);
    return this.filterResourcesByType(resourcesFiltered, resourceType);
  }*/
  
  public static filterResourcesByType(resources: ResourceDoc[], resourceType: ResourceType): ResourceDoc[] {
    switch (resourceType) {
      case 'materials':
        return resources.filter(resource => resource.type === 'MATERIAL');
      case 'equipment':
        return resources.filter(resource => resource.group === 'equipment');
      default:
        return resources.filter(resource => resource.role === true);
    }
  }

  static async isResourceBeingUsed(projectId: string, resourceId: string): Promise<boolean> {
    const assignments = await this.getAssignmentsForTask(projectId, resourceId);
    return assignments.length > 0;
  }
  static async softDeleteResource(projectId: string, resource: ResourceDoc, user: string): Promise<any> {
    const db = this.getResourcesDb(projectId);

    const docToUpdate: ResourceDoc = {
      ...resource,
      deleted_by: user,
      deleted_date: new Date().toISOString(),
    };
    return await db.saveDoc(docToUpdate);
  }

  static getAssignmentsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-assignments`);
  }
  static async saveAssignment(projectId: string, assignment: AssignmentDoc): Promise<AssignmentDoc> {
    const db = this.getAssignmentsDb(projectId);
    return await db.saveDoc(assignment);
  }
  static async getAssignments(projectId: string): Promise<AssignmentDoc[]> {
    const db = this.getAssignmentsDb(projectId);
    const estimates = await db.getDocs();
    return estimates.filter((estimate: AssignmentDoc) => !estimate.deleted_date);
  }
  static async getAssignmentsForTask(projectId: string, taskId: string): Promise<AssignmentDoc[]> {
    const db = this.getAssignmentsDb(projectId);
    const assignments = await db.getDocs();
    return assignments.filter(assignment => assignment.task_unique_id === taskId);
  }
  static async getAssignmentsForTaskAndSection(projectId: string, taskId: string): Promise<AssignmentDoc[]> {
    const assignments = await this.getAssignmentsForTask(projectId, taskId);
    return assignments.filter(assignment => assignment.task_unique_id === parseInt(taskId));
  }
  static async softDeleteEstimate(projectId: string, assignmentDoc: AssignmentDoc, user: string): Promise<any> {
    const db = this.getAssignmentsDb(projectId);

    const estimateDeleted: AssignmentDoc = {
      ...assignmentDoc,
      deleted_by: user,
      deleted_date: new Date().toISOString(),
    };

    return db.saveDoc(estimateDeleted);
  }

  static getWorkLogsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-worklogs`);
  }
  /*
  static async getWorkLogs(projectId: string): Promise<WorkLogDoc[]> {
    const db = this.getWorkLogsDb(projectId);
    return await db.getDocs();
  }
    */
  static async saveWorkLog(projectId: string, workLog: WorkLogDoc): Promise<WorkLogDoc> {
    const db = this.getWorkLogsDb(projectId);
    return await db.saveDoc(workLog);
  }
  static async getWorkLogsForAssignment(projectId: string, assignmentId: string): Promise<WorkLogDoc[]> {
    const db = this.getWorkLogsDb(projectId);
    const workLogs = await db.getDocs();
    return workLogs.filter(log => log.assignment_id === assignmentId);
  }
  static async getWorkLogsForDate(projectId: string, date: string): Promise<WorkLogDoc[]> {
    const db = this.getWorkLogsDb(projectId);
    const workLogs = await db.getDocs();
    return workLogs.filter(log => {
      const logDate = new Date(log.start).toISOString().split('T')[0];
      return logDate === date;
    });
  }

  static getLeafTasks(task: Task): Task[] {
    const leafTasks: Task[] = [];
    if (task.isLeaf && task.type == TaskType.TASK) {
      leafTasks.push(task);
    } else {
      task.children.filter(child => child.type == TaskType.TASK).forEach((child: Task) => {
        leafTasks.push(...this.getLeafTasks(child));
      });
    }
    return leafTasks;
  }

  /**
   * Gets the total duration of all the leaf nodes of a task
   */
  static getCumulativeDuration(task: Task): number {
    return task.isLeaf ? (task.doc.duration ?? 0) : (task.children as Task[]).reduce((sum, child) => sum + this.getCumulativeDuration(child), 0);
  }

  static getNextResourceUniqueId(resources: ResourceDoc[]): number {
    return resources.reduce((max, resource) => Math.max(max, resource.unique_id), 0) + 1;
  }

  static getNextAssignmentUniqueId(assignments: AssignmentDoc[]): number {
    return assignments.reduce((max, assignment) => Math.max(max, assignment.unique_id), 0) + 1;
  }
}
